import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"
import {
  CollaborationState,
  SimulationRoom,
  Participant,
  CollaborativeChoice,
  Vote,
  VotingStatus,
  ParticipantStatus,
  WebSocketMessage,
  ChatMessage,
  TranscriptionSegment,
  TranscriptionState,
  PrivateConversation,
  SessionTranscript
} from "./types"

const initialState: CollaborationState = {
  currentRoom: null,
  participants: [],
  pendingChoices: [],
  completedChoices: [],
  chatMessages: [],
  privateConversations: [],
  activeConversationId: null,
  isConnected: false,
  currentUserId: null,
  votingModalOpen: false,
  currentVotingChoice: null,

  // Transcription state
  transcription: {
    segments: [],
    partialSegments: [],
    isTranscriptionEnabled: false,
    isTranscriptionActive: false,
    transcriptionErrors: []
  } as TranscriptionState,

  // Admin transcript storage
  sessionTranscripts: [],
  currentSessionTranscript: null
}

export const collaborationSlice = createSlice({
  name: "collaborationSlice",
  initialState,
  reducers: {
    setCurrentRoom(state, action: PayloadAction<SimulationRoom | null>) {
      state.currentRoom = action.payload
    },

    setCurrentUserId(state, action: PayloadAction<number>) {
      state.currentUserId = action.payload
    },

    addParticipant(state, action: PayloadAction<Participant>) {
      const existingIndex = state.participants.findIndex(
        p => p.id === action.payload.id
      )
      if (existingIndex >= 0) {
        state.participants[existingIndex] = action.payload
      } else {
        state.participants.push(action.payload)
      }
      
      // Update room participant count
      if (state.currentRoom) {
        state.currentRoom.currentParticipants = state.participants.length
      }
    },

    removeParticipant(state, action: PayloadAction<string>) {
      state.participants = state.participants.filter(
        p => p.id !== action.payload
      )
      
      // Update room participant count
      if (state.currentRoom) {
        state.currentRoom.currentParticipants = state.participants.length
      }
    },

    updateParticipantStatus(
      state, 
      action: PayloadAction<{ participantId: string; status: ParticipantStatus }>
    ) {
      const participant = state.participants.find(
        p => p.id === action.payload.participantId
      )
      if (participant) {
        participant.status = action.payload.status
        participant.lastActive = new Date().toISOString()
      }
    },

    setParticipants(state, action: PayloadAction<Participant[]>) {
      state.participants = action.payload
      
      // Update room participant count
      if (state.currentRoom) {
        state.currentRoom.currentParticipants = action.payload.length
      }
    },

    addCollaborativeChoice(state, action: PayloadAction<CollaborativeChoice>) {
      // Remove any existing choice with the same ID
      state.pendingChoices = state.pendingChoices.filter(
        c => c.id !== action.payload.id
      )
      state.pendingChoices.push(action.payload)
    },

    updateCollaborativeChoice(state, action: PayloadAction<CollaborativeChoice>) {
      const index = state.pendingChoices.findIndex(
        c => c.id === action.payload.id
      )
      if (index >= 0) {
        state.pendingChoices[index] = action.payload

        // If choice is resolved, move to completed choices
        if (action.payload.status === VotingStatus.APPROVED ||
            action.payload.status === VotingStatus.REJECTED ||
            action.payload.status === VotingStatus.EXPIRED) {
          const completedChoice = state.pendingChoices.splice(index, 1)[0]
          state.completedChoices.push(completedChoice)
        }
      }
    },

    castVote(
      state,
      action: PayloadAction<{ choiceId: string; vote: Vote }>
    ) {
      const choice = state.pendingChoices.find(
        c => c.id === action.payload.choiceId
      )
      if (choice) {
        // Remove any existing vote from this participant
        choice.votes = choice.votes.filter(
          v => v.participantId !== action.payload.vote.participantId
        )
        // Add the new vote
        choice.votes.push(action.payload.vote)
      }

      // Also update currentVotingChoice if it matches - this ensures the modal
      // sees vote updates in real-time from other participants via LiveKit
      if (state.currentVotingChoice?.id === action.payload.choiceId) {
        state.currentVotingChoice.votes = state.currentVotingChoice.votes.filter(
          v => v.participantId !== action.payload.vote.participantId
        )
        state.currentVotingChoice.votes.push(action.payload.vote)
      }
    },

    setConnectionStatus(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload
    },

    openVotingModal(state, action: PayloadAction<CollaborativeChoice>) {
      state.votingModalOpen = true
      state.currentVotingChoice = action.payload
    },

    closeVotingModal(state) {
      state.votingModalOpen = false
      state.currentVotingChoice = null
    },

    setPendingChoices(state, action: PayloadAction<CollaborativeChoice[]>) {
      state.pendingChoices = action.payload
    },

    setCompletedChoices(state, action: PayloadAction<CollaborativeChoice[]>) {
      state.completedChoices = action.payload
    },

    resetCollaboration(state) {
      return {
        ...initialState,
        currentUserId: state.currentUserId // Preserve user ID
      }
    },

    // Chat message actions
    addChatMessage(state, action: PayloadAction<ChatMessage>) {
      state.chatMessages.push(action.payload)
    },

    setChatMessages(state, action: PayloadAction<ChatMessage[]>) {
      state.chatMessages = action.payload
    },

    clearChatMessages(state) {
      state.chatMessages = []
    },

    // Handle WebSocket messages
    handleWebSocketMessage(state, action: PayloadAction<WebSocketMessage>) {
      const { type, payload } = action.payload
      
      switch (type) {
        case "participant_joined":
          collaborationSlice.caseReducers.addParticipant(state, { 
            type: "", 
            payload: payload as Participant 
          })
          break
          
        case "participant_left":
          collaborationSlice.caseReducers.removeParticipant(state, {
            type: "",
            payload: (payload as { participantId: string }).participantId
          })
          break
          
        case "choice_proposed":
          collaborationSlice.caseReducers.addCollaborativeChoice(state, { 
            type: "", 
            payload: payload as CollaborativeChoice 
          })
          break
          
        case "vote_cast":
          collaborationSlice.caseReducers.castVote(state, {
            type: "",
            payload: payload as { choiceId: string; vote: Vote }
          })
          break
          
        case "choice_resolved":
          collaborationSlice.caseReducers.updateCollaborativeChoice(state, { 
            type: "", 
            payload: payload as CollaborativeChoice 
          })
          break

        case "chat_message":
          collaborationSlice.caseReducers.addChatMessage(state, { 
            type: "", 
            payload: payload as ChatMessage 
          })
          break

        case "transcription_segment":
          collaborationSlice.caseReducers.addTranscription(state, { 
            type: "", 
            payload: payload as TranscriptionSegment 
          })
          break

        case "transcription_error":
          collaborationSlice.caseReducers.addTranscriptionError(state, {
            type: "",
            payload: (payload as { error: string }).error
          })
          break
      }
    },

    // Transcription actions
    addTranscription(state, action: PayloadAction<TranscriptionSegment>) {
      const segment = action.payload
      
      if (segment.isFinal) {
        // Remove any partial segment with the same ID
        state.transcription.partialSegments = state.transcription.partialSegments.filter(
          s => s.id !== segment.id
        )
        
        // Add to final segments
        const existingIndex = state.transcription.segments.findIndex(s => s.id === segment.id)
        if (existingIndex >= 0) {
          state.transcription.segments[existingIndex] = segment
        } else {
          state.transcription.segments.push(segment)
        }
      } else {
        // Update partial segments
        const existingIndex = state.transcription.partialSegments.findIndex(s => s.id === segment.id)
        if (existingIndex >= 0) {
          state.transcription.partialSegments[existingIndex] = segment
        } else {
          state.transcription.partialSegments.push(segment)
        }
      }
    },

    updateTranscription(state, action: PayloadAction<TranscriptionSegment>) {
      const segment = action.payload
      
      if (segment.isFinal) {
        collaborationSlice.caseReducers.addTranscription(state, action)
      } else {
        // Update partial segments
        const existingIndex = state.transcription.partialSegments.findIndex(s => s.id === segment.id)
        if (existingIndex >= 0) {
          state.transcription.partialSegments[existingIndex] = segment
        } else {
          state.transcription.partialSegments.push(segment)
        }
      }
    },

    setTranscriptionEnabled(state, action: PayloadAction<boolean>) {
      state.transcription.isTranscriptionEnabled = action.payload
    },

    setTranscriptionActive(state, action: PayloadAction<boolean>) {
      state.transcription.isTranscriptionActive = action.payload
    },

    addTranscriptionError(state, action: PayloadAction<string>) {
      state.transcription.transcriptionErrors.push(action.payload)
      
      // Keep only last 10 errors
      if (state.transcription.transcriptionErrors.length > 10) {
        state.transcription.transcriptionErrors = state.transcription.transcriptionErrors.slice(-10)
      }
    },

    clearTranscriptionErrors(state) {
      state.transcription.transcriptionErrors = []
    },

    clearTranscriptions(state) {
      state.transcription.segments = []
      state.transcription.partialSegments = []
    },

    // Admin transcript storage actions
    storeSessionTranscript(state, action: PayloadAction<SessionTranscript>) {
      // Replace if session already exists, otherwise add new
      const existingIndex = state.sessionTranscripts.findIndex(
        transcript => transcript.sessionId === action.payload.sessionId
      )

      if (existingIndex >= 0) {
        state.sessionTranscripts[existingIndex] = action.payload
      } else {
        state.sessionTranscripts.push(action.payload)
      }

      // Update current session transcript
      state.currentSessionTranscript = action.payload
    },

    setCurrentSessionTranscript(state, action: PayloadAction<SessionTranscript>) {
      state.currentSessionTranscript = action.payload
    },

    clearSessionTranscripts(state) {
      state.sessionTranscripts = []
      state.currentSessionTranscript = null
    },

    // Private messaging actions
    createPrivateConversation(state, action: PayloadAction<{
      participantIds: number[]
      roomId: string
      currentUserId: number
    }>) {
      const { participantIds, roomId, currentUserId } = action.payload
      
      // Include current user in conversation
      const allParticipantIds = Array.from(new Set([currentUserId, ...participantIds])).sort()
      
      // Check if conversation already exists
      const existingConversation = state.privateConversations.find(conv => 
        conv.participantIds.length === allParticipantIds.length &&
        conv.participantIds.every(id => allParticipantIds.includes(id))
      )
      
      if (existingConversation) {
        state.activeConversationId = existingConversation.id
        return
      }
      
      // Create new conversation
      const participantNames = allParticipantIds.map(id => {
        const participant = state.participants.find(p => p.userId === id)
        return participant ? `${participant.firstName} ${participant.lastName}` : `User ${id}`
      })
      
      const conversationId = `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      
      const newConversation: PrivateConversation = {
        id: conversationId,
        participantIds: allParticipantIds,
        participantNames,
        lastMessageAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        roomId
      }
      
      state.privateConversations.push(newConversation)
      state.activeConversationId = conversationId
    },

    setActiveConversation(state, action: PayloadAction<string | null>) {
      state.activeConversationId = action.payload
    },

    addPrivateMessage(state, action: PayloadAction<ChatMessage>) {
      const message = action.payload
      state.chatMessages.push(message)
      
      // Update conversation's last message
      if (message.conversationId) {
        const conversation = state.privateConversations.find(c => c.id === message.conversationId)
        if (conversation) {
          conversation.lastMessage = message
          conversation.lastMessageAt = message.timestamp
        }
      }
    },

    updateConversationLastSeen() {
      // This could be used for unread message tracking
      // Implementation depends on if you want to track read receipts
      // Parameters: _state, _action: PayloadAction<{ conversationId: string, userId: number, timestamp: string }>
    }
  }
})

export const collaborationReducer = persistReducer(
  {
    key: "msk:collaboration",
    storage,
    whitelist: [
      "currentRoom",
      "participants",
      "pendingChoices",
      "completedChoices",
      "currentUserId"
    ], // Don't persist connection status or modal state
  },
  collaborationSlice.reducer
)

export const {
  setCurrentRoom,
  setCurrentUserId,
  addParticipant,
  removeParticipant,
  updateParticipantStatus,
  setParticipants,
  addCollaborativeChoice,
  updateCollaborativeChoice,
  castVote,
  openVotingModal,
  closeVotingModal,
  setConnectionStatus,
  addChatMessage,
  setChatMessages,
  clearChatMessages,
  handleWebSocketMessage,
  addTranscription,
  updateTranscription,
  setTranscriptionEnabled,
  setTranscriptionActive,
  addTranscriptionError,
  clearTranscriptionErrors,
  clearTranscriptions,
  storeSessionTranscript,
  setCurrentSessionTranscript,
  clearSessionTranscripts,
  createPrivateConversation,
  setActiveConversation,
  addPrivateMessage,
  updateConversationLastSeen,
  resetCollaboration,
  setPendingChoices,
  setCompletedChoices
} = collaborationSlice.actions 